/**
 * Subscribing a device to wicket and result notifications.
 *
 * ## What went wrong last time
 *
 * A "Get notified" button shipped and never worked, in two independent ways (#102 removed it).
 * It awaited `navigator.serviceWorker.ready`, which with no registered worker is a promise
 * that **never settles** — not rejects, never settles — so the tap blocked forever, no state
 * changed, and the `catch` written to make it best-effort never ran. And it called
 * `pushManager.subscribe` with no `applicationServerKey`, which Chrome requires.
 *
 * Both are fixed here by construction: `ready` is raced against a timeout so it can never hang
 * silently, and the key is fetched before anything is attempted. The lesson worth keeping is
 * the first one — an `await` on a promise that never settles is invisible to every error path
 * you have.
 *
 * ## The permission is the expensive part
 *
 * Not the plumbing. `Notification.requestPermission` can be asked **once**; a denial is not
 * re-askable, and on iOS the only way back is deleting the app. So it is only ever reached
 * from a tap on a control that says what it is for, and `canOffer` is what keeps the control
 * off the screen when it could not work anyway.
 */
import { api } from "./api.ts";
import { localStore } from "./localStore.ts";

/**
 * Which matches this device has subscribed to.
 *
 * A subscription is per device *and* per match, and the browser cannot tell us which matches
 * it holds — `pushManager` knows only that this origin has one subscription. So the honest
 * answer to "am I subscribed to this match" is a note this device made when it subscribed.
 *
 * It can go stale in exactly one direction: the server prunes a row after a push service
 * reports the endpoint gone, and this device still thinks it is subscribed. The cost is one
 * misleading label until the next tap, and the tap re-subscribes — which is why the server
 * route is an upsert. Read through `localStore()`, which already tolerates a browser that
 * refuses storage.
 */
const SUBSCRIBED_KEY = "sportsos.push.matches";

const subscribedSet = (): Set<string> => {
  try {
    const raw = localStore().getItem(SUBSCRIBED_KEY);
    return new Set(raw === null ? [] : (JSON.parse(raw) as string[]));
  } catch {
    return new Set();
  }
};

const write = (matches: Set<string>): void => {
  try {
    localStore().setItem(SUBSCRIBED_KEY, JSON.stringify([...matches]));
  } catch {
    // A browser refusing storage. The label is cosmetic; the subscription itself is on the
    // server either way.
  }
};

/** Whether this device believes it is subscribed to this match. */
export const isSubscribedHere = (matchId: string): boolean => subscribedSet().has(matchId);

const rememberLocally = (matchId: string): void => {
  const matches = subscribedSet();
  matches.add(matchId);
  write(matches);
};

const forgetLocally = (matchId: string): void => {
  const matches = subscribedSet();
  matches.delete(matchId);
  write(matches);
};

/** How long to wait for a service worker before giving up on the whole idea. */
const READY_TIMEOUT_MS = 5000;

export type SubscribeOutcome =
  | "subscribed"
  | "denied"
  /** No worker, no push support, or no key configured. The control should not have been shown. */
  | "unavailable"
  | "failed";

/**
 * Whether this browser could subscribe at all, before anything is offered on screen.
 *
 * Deliberately does **not** check `Notification.permission === "default"`. Someone who has
 * already granted permission should still see the control, because the subscription is
 * per-match and they will want it again on the next one.
 */
export function canOffer(): boolean {
  try {
    return (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window &&
      Notification.permission !== "denied"
    );
  } catch {
    return false;
  }
}

/**
 * `navigator.serviceWorker.ready`, but bounded.
 *
 * **The bug that made the last attempt invisible.** With no registration that promise never
 * settles, so an `await` on it is not a slow path — it is a dead one, and nothing downstream
 * ever runs, including the error handling. A timeout turns a hang into an outcome.
 */
async function workerReady(): Promise<ServiceWorkerRegistration | null> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), READY_TIMEOUT_MS)),
  ]);
}

/**
 * Chrome wants the application server key as bytes, not as the base64url string the server
 * sends. Safari accepts the string; Chrome throws on it.
 */
function decodeKey(base64url: string): ArrayBuffer {
  const padded = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), "=");
  const binary = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  // An `ArrayBuffer` rather than the `Uint8Array` that is the obvious thing to return: the
  // DOM types for `applicationServerKey` accept `BufferSource`, and a `Uint8Array` is only
  // assignable to that when its backing buffer is known not to be shared. Handing over the
  // buffer sidesteps a cast.
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/**
 * Ask for permission and subscribe this device to one match.
 *
 * Called only from a tap, with the reason already on screen — see the note at the top of this
 * file about why that is not a style preference.
 */
export async function subscribe(matchId: string): Promise<SubscribeOutcome> {
  try {
    if (!canOffer()) return "unavailable";

    // The key first, because it is the cheapest way to discover that push is not configured on
    // this deployment — and asking for notification permission we then cannot use would spend
    // the one question we get.
    const { publicKey } = await api.pushKey();
    if (publicKey === null) return "unavailable";

    const registration = await workerReady();
    if (registration === null || !("pushManager" in registration)) return "unavailable";

    // Must be the direct result of a user gesture on iOS. It is, because this whole function
    // is only ever reached from an onClick.
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return "denied";

    const subscription = await registration.pushManager.subscribe({
      // Mandatory everywhere. A push that shows nothing gets the origin's permission revoked.
      userVisibleOnly: true,
      // **The second thing the last attempt got wrong.** Chrome rejects a subscription with no
      // application server key, so without this the call fails even when everything else is
      // right.
      applicationServerKey: decodeKey(publicKey),
    });

    const keys = subscription.toJSON().keys ?? {};
    if (!keys["p256dh"] || !keys["auth"]) return "failed";

    await api.pushSubscribe({
      matchId: matchId as never,
      endpoint: subscription.endpoint,
      keys: { p256dh: keys["p256dh"], auth: keys["auth"] },
    });
    rememberLocally(matchId);
    return "subscribed";
  } catch {
    // Every failure is reported rather than swallowed, which is the whole difference from the
    // previous attempt: the caller puts something on screen either way.
    return "failed";
  }
}

/**
 * Stop notifications for one match.
 *
 * **Deletes the server row and nothing else.** The obvious extra step — calling
 * `subscription.unsubscribe()` on the browser — is wrong here and was in an earlier draft of
 * this file: `pushManager` holds **one subscription per registration**, not one per match,
 * while `push_subscriptions` is keyed `(user_id, match_id)`. Tearing it down to leave match A
 * would silently break every other match this device is still following, and those rows would
 * survive until a 410 eventually pruned them — so the symptom would be notifications stopping
 * for a match nobody turned off.
 *
 * Leaving the browser subscription alive costs nothing: the server simply has no row for this
 * match, so it sends nothing for it.
 */
export async function unsubscribe(matchId: string): Promise<boolean> {
  try {
    await api.pushUnsubscribe(matchId);
    forgetLocally(matchId);
    return true;
  } catch {
    return false;
  }
}
